import { capitalizeFirstLetter } from '@winging/util/helper/string.util';
import { useTranslations } from 'next-intl';
import React, { useId, useState } from 'react';
import { UserType } from '@winging/util/type/user.type';
import CloseIcon from '@/shared/ui/icon/CloseIcon';
import { useAuth } from '@/shared/providers/AuthProvider';
import { Link } from '@/i18n/navigation';

interface GeneralMobileSidebarProps {
  showMobileSidebar: boolean;
  setShowMobileSidebar: (showMobileSidebar: boolean) => void;
  onInboxNavigation?: () => void;
}

const GeneralMobileSidebar = ({
  showMobileSidebar,
  setShowMobileSidebar,
  onInboxNavigation,
}: GeneralMobileSidebarProps) => {
  const { user, signOut } = useAuth();
  const t = useTranslations('Header');
  const [showDashboardSubMenu, setShowDashboardSubmenu] =
    useState<boolean>(false);
  // 대시보드 서브메뉴 토글 버튼과 펼쳐지는 영역을 aria-controls로 연결하는 id
  const dashboardSubMenuId = useId();

  return (
    <div
      style={{ boxShadow: '-4px 4px 4px 0 rgba(0, 0, 0, 0.25)' }}
      className={
        'main:hidden fixed top-0 right-0 z-50 flex h-screen w-[266px] flex-col bg-white pt-[20px]'
      }
    >
      <div className={'relative h-full w-full'}>
        <div className={'flex h-full flex-col'}>
          {user && (
            <>
              <div className={'flex flex-col pt-[14px] pb-[34px] pl-[42px]'}>
                <div className={'text-body1 mb-[18px]'}>
                  {/* 사용자 이름 뒤 호칭은 locale별로 다르게 표시한다. */}
                  {user.email.split('@')[0]}
                  {t('님')}
                </div>
                {user.userType === UserType.Personal && (
                  <div className={'flex items-center gap-[12px]'}>
                    <svg
                      className={'mt-[-3px]'}
                      width="16"
                      height="22"
                      viewBox="0 0 16 22"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M12.3211 12.012C13.3275 12.6192 14 13.6935 14 14.9058V18.2C14 18.8273 13.4606 19.3411 12.8021 19.3411H3.75843C3.09995 19.3411 2.56055 18.8273 2.56055 18.2V14.9058C2.56055 13.6913 3.23304 12.6192 4.23945 12.012C5.30891 12.9306 6.72629 13.4911 8.27911 13.4911C9.83192 13.4911 11.2493 12.9328 12.3188 12.012M8.28144 4C10.4507 4 12.209 5.67491 12.209 7.74129C12.209 9.80767 10.4507 11.4826 8.28144 11.4826C6.11217 11.4826 4.35387 9.80767 4.35387 7.74129C4.35387 5.67491 6.11217 4 8.28144 4Z"
                        fill="#303544"
                      />
                    </svg>
                    <span className={'text-label2 text-gray-800'}>
                      {t('개인회원')}
                    </span>
                  </div>
                )}
                {user.userType === UserType.Business && (
                  <div className={'flex items-center gap-[12px]'}>
                    <svg
                      width="18"
                      height="14"
                      viewBox="0 0 18 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g clipPath="url(#clip0_934_23107)">
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M17.048 7.43629C17.619 7.84226 17.9996 8.55677 17.9996 9.36466V11.561C17.9996 11.9791 17.6933 12.3222 17.3199 12.3222H13.9649V9.95332C13.9649 9.33218 13.8199 8.74352 13.568 8.23199C13.5589 8.2117 13.5481 8.1914 13.5372 8.17313C13.916 8.33552 14.3256 8.4228 14.7552 8.4228C15.6361 8.4228 16.439 8.04931 17.0462 7.43832M14.757 2.0957C15.9877 2.0957 16.9828 3.21213 16.9828 4.58838C16.9828 5.96463 15.9859 7.08106 14.757 7.08106C13.5281 7.08106 12.5312 5.96463 12.5312 4.58838C12.5312 3.21213 13.5281 2.0957 14.757 2.0957Z"
                          fill="#303544"
                        />
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M4.03283 12.3222H0.67969C0.306314 12.3222 0 11.9791 0 11.561V9.36466C0 8.55474 0.380626 7.84226 0.951566 7.43629C1.55876 8.04931 2.3617 8.42077 3.24257 8.42077C3.67214 8.42077 4.08176 8.33349 4.46058 8.1711C4.4497 8.1914 4.44064 8.20967 4.43158 8.22996C4.17783 8.74149 4.03464 9.33015 4.03464 9.95129V12.3201L4.03283 12.3222ZM3.24257 2.0957C4.47145 2.0957 5.46833 3.21213 5.46833 4.58838C5.46833 5.96463 4.47145 7.08106 3.24257 7.08106C2.01369 7.08106 1.01682 5.96463 1.01682 4.58838C1.01682 3.21213 2.01369 2.0957 3.24257 2.0957Z"
                          fill="#303544"
                        />
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M12.1368 7.31158C12.918 7.86574 13.44 8.84616 13.44 9.95244V12.9587C13.44 13.5311 13.0213 14 12.5102 14H5.49036C4.97924 14 4.56055 13.5311 4.56055 12.9587V9.95244C4.56055 8.84414 5.08255 7.86574 5.86374 7.31158C6.69387 8.14992 7.79406 8.66145 8.99938 8.66145C10.2047 8.66145 11.3049 8.15195 12.135 7.31158M9.00119 0C10.685 0 12.0498 1.52849 12.0498 3.41424C12.0498 5.29999 10.685 6.82848 9.00119 6.82848C7.31737 6.82848 5.95255 5.29999 5.95255 3.41424C5.95255 1.52849 7.31737 0 9.00119 0Z"
                          fill="#303544"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_934_23107">
                          <rect width="18" height="14" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                    <span className={'text-label2 text-gray-800'}>
                      {t('기업회원')} {capitalizeFirstLetter(user?.role || '')}
                    </span>
                  </div>
                )}
                <div className={'mt-[6px] flex items-center gap-[12px]'}>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g clipPath="url(#clip0_934_23118)">
                      <path
                        d="M7.82227 14.6362V15.5811C8.38461 15.5759 8.93772 15.5464 9.47744 15.4938V14.5488C8.93929 14.6011 8.38636 14.6307 7.82227 14.6362Z"
                        fill="#ED8118"
                      />
                      <path
                        d="M0.136197 7.83789C-0.0340128 9.31647 0.366446 10.7352 1.2011 11.9669V10.644C0.622273 9.79005 0.250918 8.84189 0.136197 7.83789Z"
                        fill="#ED8118"
                      />
                      <path
                        d="M3.96094 13.5156V14.5847C4.30912 14.7679 4.67795 14.9285 5.06439 15.064V13.9956C4.67884 13.8601 4.30993 13.6993 3.96094 13.5156Z"
                        fill="#ED8118"
                      />
                      <path
                        d="M13.8926 11.8908V13.0001C14.0988 12.7052 14.2837 12.3857 14.4443 12.0452V10.9355C14.284 11.2754 14.0992 11.5952 13.8926 11.8908Z"
                        fill="#ED8118"
                      />
                      <path
                        d="M14.9941 10.2351V11.4508C15.2691 10.7431 15.4509 9.96602 15.5178 9.14076C15.5535 8.69957 15.5545 8.26403 15.5243 7.83789C15.4649 8.69 15.2808 9.49835 14.9941 10.2351Z"
                        fill="#ED8118"
                      />
                      <path
                        d="M12.2363 13.0792V14.0328C12.6297 13.8538 12.9988 13.655 13.3398 13.4386V12.4844C12.9994 12.7007 12.6304 12.8998 12.2363 13.0792Z"
                        fill="#ED8118"
                      />
                      <path
                        d="M2.30469 12.4824V13.7706C2.6457 14.0529 3.01441 14.313 3.40814 14.5469V13.258C3.01524 13.0245 2.64627 12.7649 2.30469 12.4824Z"
                        fill="#ED8118"
                      />
                      <path
                        d="M9.47852 14.3645V15.5816C9.85761 15.4991 10.2263 15.3875 10.582 15.2494V14.0332C10.2272 14.1708 9.85853 14.2821 9.47852 14.3645Z"
                        fill="#ED8118"
                      />
                      <path
                        d="M6.71875 14.5488V15.5041C6.88609 15.5251 7.05537 15.5423 7.22678 15.555C7.42645 15.5697 7.62499 15.5782 7.8222 15.5811V14.6248C7.44686 14.6197 7.07842 14.5939 6.71875 14.5488Z"
                        fill="#ED8118"
                      />
                      <path
                        d="M11.1328 13.6564V15.0645C11.5182 14.8786 11.8872 14.6588 12.2363 14.4083V13C11.8879 13.2502 11.519 13.4702 11.1328 13.6564Z"
                        fill="#ED8118"
                      />
                      <path
                        d="M5.06445 14.0332V15.2635C5.4187 15.3965 5.78721 15.5034 6.1679 15.5816V14.352C5.78824 14.2739 5.41961 14.1668 5.06445 14.0332Z"
                        fill="#ED8118"
                      />
                      <path
                        d="M1.20117 10.9355V12.3504C1.52483 12.7646 1.89398 13.155 2.30462 13.5162V12.0998C1.89473 11.7396 1.52533 11.35 1.20117 10.9355Z"
                        fill="#ED8118"
                      />
                      <path
                        d="M3.4082 13V14.3529C3.58901 14.4201 3.773 14.4853 3.95993 14.5484V13.1954C3.77317 13.1323 3.58905 13.0673 3.4082 13Z"
                        fill="#C54B17"
                      />
                      <path
                        d="M6.16797 14.0332V15.5179C6.35046 15.5413 6.53439 15.5625 6.71969 15.5816V14.0968C6.53453 14.0777 6.35057 14.0566 6.16797 14.0332Z"
                        fill="#C54B17"
                      />
                      <path
                        d="M13.3398 12.2574V13.5152C13.5289 13.4208 13.7131 13.3241 13.8916 13.2248V11.9668C13.7133 12.0662 13.529 12.163 13.3398 12.2574Z"
                        fill="#C54B17"
                      />
                      <path
                        d="M14.4434 10.8831V11.9664C14.6412 11.8152 14.8255 11.6598 14.9951 11.5005V10.418C14.8257 10.5769 14.6413 10.732 14.4434 10.8831Z"
                        fill="#C54B17"
                      />
                      <path
                        d="M10.582 14.1188V15.0655C10.7676 15.0382 10.9518 15.0098 11.1338 14.9799V14.0332C10.9519 14.0631 10.7677 14.0915 10.582 14.1188Z"
                        fill="#C54B17"
                      />
                      <path
                        d="M1.25783 11.1332C1.50294 11.5019 1.78226 11.8487 2.09219 12.1692C2.18501 12.2652 2.28061 12.3588 2.37876 12.45C2.67622 12.7264 2.99752 12.9804 3.33967 13.2088C3.4336 13.2716 3.52924 13.3322 3.62624 13.391C3.98365 13.6077 4.36145 13.7974 4.7563 13.9573C4.85087 13.9956 4.9464 14.0322 5.04288 14.067C5.42216 14.2039 5.81582 14.3136 6.22126 14.3935C6.32207 14.4134 6.42363 14.4313 6.52586 14.4475C6.90158 14.5069 7.28645 14.5409 7.67852 14.5476C7.72621 14.5484 7.77389 14.5493 7.82179 14.5493C7.8697 14.5493 7.91739 14.5484 7.96509 14.5476C8.39244 14.5403 8.81133 14.5007 9.21903 14.431C9.32129 14.4136 9.42282 14.3941 9.52363 14.3729C9.92945 14.2875 10.3231 14.1723 10.702 14.0297C10.7985 13.9934 10.8941 13.9556 10.9886 13.9159C11.3841 13.7493 11.7619 13.5525 12.1186 13.3286C12.2158 13.2677 12.3113 13.2046 12.4052 13.1397C12.7484 12.9027 13.0697 12.6396 13.3661 12.3538C13.4644 12.2591 13.5601 12.1621 13.6527 12.0624C13.9651 11.7263 14.2446 11.3627 14.4871 10.9764C14.5975 10.8004 14.7004 10.6197 14.795 10.4346C15.1852 9.67051 15.4359 8.83219 15.5169 7.94847C15.5357 7.74243 15.5459 7.53407 15.5459 7.32346C15.5459 3.33276 12.0877 0.0976562 7.82179 0.0976562C3.55587 0.0976562 0.0976562 3.33275 0.0976562 7.32346C0.0976562 7.5367 0.108127 7.74763 0.127467 7.95619C0.216066 8.91136 0.502867 9.8134 0.949903 10.6258C1.04529 10.7992 1.14826 10.9683 1.25783 11.1332Z"
                        fill="#F7A531"
                      />
                      <path
                        d="M2.85742 7.32195C2.85742 10.2845 5.27743 12.7196 8.3732 12.9994C11.4703 12.7208 13.8919 10.2854 13.8919 7.32195C13.8919 4.35846 11.4703 1.92309 8.3732 1.64453C5.27742 1.92431 2.85742 4.35936 2.85742 7.32195Z"
                        fill="#F39117"
                      />
                      <path
                        d="M7.78731 12.9994C7.83808 12.9994 7.8887 12.9987 7.93917 12.9976C7.9573 12.9971 7.97531 12.9963 7.9934 12.9957C8.02565 12.9947 8.05791 12.9938 8.09003 12.9923C8.11169 12.9913 8.13323 12.9898 8.15484 12.9886C8.18321 12.987 8.2116 12.9855 8.23987 12.9835C8.26288 12.9819 8.28577 12.9799 8.30871 12.978C8.33067 12.9763 8.35271 12.9748 8.3746 12.9728L8.37316 12.9727C5.31728 12.6942 2.92849 10.2706 2.92849 7.32196C2.92849 4.37332 5.31729 1.94973 8.37316 1.67126L8.3746 1.67111C8.3527 1.66912 8.33066 1.66764 8.30871 1.66587C8.28577 1.66402 8.26288 1.66201 8.23987 1.6604C8.21144 1.65841 8.18288 1.65691 8.15434 1.6553C8.13295 1.65409 8.11162 1.65265 8.09015 1.65165C8.05778 1.65015 8.02527 1.6492 7.99276 1.64817C7.97489 1.6476 7.95709 1.64675 7.93918 1.64634C7.88871 1.64517 7.83808 1.64453 7.78731 1.64453C4.45516 1.64453 1.75391 4.18639 1.75391 7.32195C1.75391 10.4575 4.45516 12.9994 7.78731 12.9994Z"
                        fill="#EE7016"
                      />
                      <path
                        d="M10.9823 6.78071L9.86183 7.94377C9.75932 8.05017 9.71097 8.20526 9.73256 8.35847L9.96848 10.033C10.0228 10.4188 9.65134 10.7057 9.33983 10.5185L7.98781 9.70586C7.86411 9.6315 7.71507 9.62886 7.58924 9.69878L6.2141 10.463C6.19883 10.4715 6.1834 10.4779 6.16797 10.4842C6.21577 10.7778 6.51487 10.9686 6.78221 10.82L8.15736 10.0558C8.28318 9.98587 8.43222 9.98852 8.55592 10.0629L9.90794 10.8755C10.2195 11.0627 10.591 10.7758 10.5366 10.39L10.3007 8.71547C10.2791 8.56227 10.3274 8.40718 10.4299 8.30077L11.5505 7.13771C11.8086 6.86975 11.6753 6.39251 11.3249 6.33014L11.0941 6.28906C11.1435 6.45209 11.1144 6.64367 10.9823 6.78071Z"
                        fill="#EE7016"
                      />
                      <path
                        d="M9.26238 5.73726L9.47845 5.773L8.8712 4.46266C8.77625 4.25777 8.55796 4.18494 8.375 4.24495L8.95838 5.50379C9.01594 5.628 9.12963 5.71531 9.26238 5.73726Z"
                        fill="#EE7016"
                      />
                      <path
                        d="M4.63782 6.92973L5.73376 8.00279C5.83403 8.10096 5.8782 8.24084 5.85189 8.37683L5.56432 9.86322C5.50125 10.1892 5.82999 10.4466 6.13756 10.3352C6.15313 10.3295 6.16872 10.3238 6.18412 10.3162L7.57208 9.63409C7.69907 9.57168 7.8495 9.57403 7.97436 9.6404L9.33898 10.3657C9.65339 10.5328 10.0283 10.2768 9.97349 9.9324L9.73537 8.43776C9.71359 8.30101 9.76238 8.16259 9.86585 8.06761L10.9968 7.02949C11.1301 6.90717 11.1595 6.73617 11.1096 6.59067C11.0619 6.45166 10.942 6.33588 10.7692 6.30868L9.46262 6.10305L9.23401 6.06707C9.09356 6.04496 8.97329 5.95706 8.91238 5.83199L8.29516 4.56455L8.24673 4.46509C8.09336 4.15015 7.63525 4.14296 7.47151 4.45292L6.76088 5.79824C6.69586 5.92133 6.57273 6.00541 6.4316 6.02311L4.88926 6.21642C4.53391 6.26097 4.38532 6.68249 4.63782 6.92973Z"
                        fill="#FDC74E"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_934_23118">
                        <rect width="16" height="16" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                  <span className={'text-label2 text-gray-500'}>
                    {(user?.company_ref?.credit || 0).toLocaleString()}{' '}
                    {t('크레딧')}
                  </span>
                </div>
              </div>
              <div
                className={
                  'h-[2px] w-full bg-gray-50 shadow-[inset_0px_4px_4px_0px_rgba(0,0,0,0.07)]'
                }
              ></div>
            </>
          )}
          <div className={'relative grow pt-[10px]'}>
            <ul className={'text-label2 flex flex-col text-gray-800'}>
              {!user && (
                <li className={'flex h-[46px] items-center px-[30px]'}>
                  <Link href={'/auth/sign-in'} className={'font-[700]'}>
                    {t('로그인')}
                  </Link>
                </li>
              )}
              <li className={'flex h-[46px] items-center px-[30px]'}>
                <Link href={'/'} className={''}>
                  {t('서비스 소개')}
                </Link>
              </li>
              <li className={'flex h-[46px] items-center px-[30px]'}>
                <Link href={'/pricing'} className={''}>
                  {t('가격 정책')}
                </Link>
              </li>
              {!!user && (
                <>
                  <li
                    className={`${showDashboardSubMenu ? 'bg-gray-50' : 'bg-white'} flex h-[46px] items-center px-[30px]`}
                  >
                    <button
                      aria-expanded={showDashboardSubMenu}
                      aria-controls={
                        showDashboardSubMenu ? dashboardSubMenuId : undefined
                      }
                      onClick={() => {
                        setShowDashboardSubmenu(true);
                      }}
                    >
                      {t('대시보드')}
                    </button>
                  </li>

                  <li className={'flex h-[46px] items-center px-[30px]'}>
                    <button onClick={signOut} className={''}>
                      {t('로그아웃')}
                    </button>
                  </li>
                </>
              )}
            </ul>
            {showDashboardSubMenu && (
              <div
                id={dashboardSubMenuId}
                className={'absolute top-0 right-0 h-full w-1/2 bg-gray-50'}
              >
                <div className={'pt-[10px]'}>
                  <ul className={'text-label2 flex flex-col text-gray-800'}>
                    <li className={'flex h-[46px] items-center px-[30px]'}>
                      <Link href={'/dashboard'} className={''}>
                        {t('에이전트')}
                      </Link>
                    </li>
                    <li className={'flex h-[46px] items-center px-[30px]'}>
                      <Link href={'/dashboard/data'} className={''}>
                        {t('데이터 관리')}
                      </Link>
                    </li>
                    <li className={'flex h-[46px] items-center px-[30px]'}>
                      <Link href={'/dashboard/specific-agents'} className={''}>
                        {t('특화 에이전트')}
                      </Link>
                    </li>
                    <li className={'flex h-[46px] items-center px-[30px]'}>
                      <Link
                        href={'/dashboard/inbox'}
                        className={''}
                        onClick={onInboxNavigation}
                      >
                        {t('알림 수신함')}
                      </Link>
                    </li>
                    <li className={'flex h-[46px] items-center px-[30px]'}>
                      <Link href={'/dashboard/billing'} className={''}>
                        {t('결제')}
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
        <button
          aria-label="닫기"
          onClick={() => setShowMobileSidebar(!showMobileSidebar)}
          className={'fixed top-[20px] right-[20px] z-50'}
        >
          <CloseIcon />
        </button>
      </div>
    </div>
  );
};

export default GeneralMobileSidebar;
